import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { listIdLinkMobileClass } from '../common/uiClasses.js';
import { canShowOgclTransferCreate, defaultItclRequestTab, hasOgclIncomingAccess } from '../../utils/networkMode.js';
import ogclTransferService from '../../services/ogclTransferService.js';
import OgclReviewConfirmModal from './OgclReviewConfirmModal.js';
import { formatUsernameLocation } from '../../utils/userDisplay';
import UnloadItclShipmentPanel from './UnloadItclShipmentPanel.js';
import {
  ITCL_REQUEST_CREATE,
  ITCL_REQUEST_LOAD_ERROR,
  ITCL_REQUEST_SUBTITLE,
  ITCL_REQUEST_TITLE,
} from './itclRequestUi.js';

const TABS = [
  { id: 'unload', label: 'Unload', statusParam: 'APPROVED', unload: true },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'pending', label: 'Pending', statusParam: 'PENDING' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
];

const statusClass = (status) => ({
  PENDING: 'bg-amber-50 text-amber-800 border-amber-200',
  APPROVED: 'bg-green-50 text-green-700 border-green-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
}[status] || 'bg-gray-50 text-gray-600 border-gray-200');

const statusLabel = (status) => ({
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
}[status] || status);

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const withinDateRange = (row, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const raw = row?.created_at || row?.requested_at || row?.submitted_at;
  if (!raw) return true;
  const day = new Date(raw);
  if (Number.isNaN(day.getTime())) return true;
  const iso = day.toISOString().slice(0, 10);
  if (fromDate && iso < fromDate) return false;
  if (toDate && iso > toDate) return false;
  return true;
};

const dashboardPathForRole = (role) => {
  if (role === 'Accountant') return '/accountant-dashboard';
  if (role === 'Agent') return '/agent-dashboard';
  if (role === 'System Admin') return '/system-admin-dashboard';
  if (role === 'Contract Party Admin') return '/contract-party-dashboard';
  return '/admin-dashboard';
};

const formatSubmitted = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN');
};

const OgclTransferRequestList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    unload: 0, unloadPending: 0, unloadUnloaded: 0, approved: 0, pending: 0, rejected: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => defaultItclRequestTab(user));
  const tabInitializedRef = useRef(Boolean(user));
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actingId, setActingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, action: 'approve', request: null });

  const role = user?.role || user?.role_name;
  const canCreate = canShowOgclTransferCreate(user);
  const canIncoming = hasOgclIncomingAccess(user);
  const isCompanyReviewer = ['System Admin', 'Admin', 'Accountant'].includes(role);
  const homePath = dashboardPathForRole(role);
  const visibleTabs = TABS.filter((tab) => {
    if (tab.id === 'unload') return canIncoming || isCompanyReviewer;
    return true;
  });
  const unloadOnly = activeTab === 'unload';
  const statusParam = unloadOnly
    ? 'APPROVED'
    : (TABS.find((t) => t.id === activeTab)?.statusParam || 'PENDING');

  const handleUnloadCount = useCallback((count) => {
    const pending = typeof count === 'object' && count != null ? Number(count.pending) || 0 : Number(count) || 0;
    const unloaded = typeof count === 'object' && count != null ? Number(count.unloaded) || 0 : 0;
    setCounts((prev) => {
      if (prev.unloadPending === pending && prev.unloadUnloaded === unloaded) return prev;
      return {
        ...prev,
        unload: pending + unloaded,
        unloadPending: pending,
        unloadUnloaded: unloaded,
      };
    });
  }, []);

  const fetchCounts = useCallback(async () => {
    const base = dateParams(fromDate, toDate);
    const unloadOnlyTab = activeTab === 'unload';
    try {
      const [incomingRes, approvedRes, pendingRes, rejectedRes] = await Promise.all([
        canIncoming || isCompanyReviewer
          ? (!unloadOnlyTab
            ? ogclTransferService.incomingShipments(base)
            : Promise.resolve({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } }),
        ogclTransferService.list({ ...base, status: 'APPROVED' }),
        ogclTransferService.list({ ...base, status: 'PENDING' }),
        ogclTransferService.list({ ...base, status: 'REJECTED' }),
      ]);
      const normalize = (res) => {
        const data = res?.data?.data || res?.data || [];
        const list = Array.isArray(data) ? data : [];
        return list.filter((row) => withinDateRange(row, fromDate, toDate)).length;
      };
      const incomingList = (incomingRes?.data?.data || incomingRes?.data || []);
      const incoming = Array.isArray(incomingList) ? incomingList.filter((row) => withinDateRange(row, fromDate, toDate)) : [];
      const unloadPending = incoming.filter((row) => !row.unloaded).length;
      const unloadUnloaded = incoming.filter((row) => row.unloaded).length;
      setCounts((prev) => ({
        unload: unloadOnlyTab ? prev.unload : unloadPending + unloadUnloaded,
        unloadPending: unloadOnlyTab ? prev.unloadPending : unloadPending,
        unloadUnloaded: unloadOnlyTab ? prev.unloadUnloaded : unloadUnloaded,
        approved: normalize(approvedRes),
        pending: normalize(pendingRes),
        rejected: normalize(rejectedRes),
      }));
    } catch {
      // ignore count errors
    }
  }, [fromDate, toDate, canIncoming, isCompanyReviewer, activeTab]);

  const fetchRows = useCallback(async () => {
    if (unloadOnly) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await ogclTransferService.list({
        ...dateParams(fromDate, toDate),
        status: statusParam,
      });
      const data = response.data?.data || response.data || [];
      const list = Array.isArray(data) ? data : [];
      setRows(list.filter((row) => withinDateRange(row, fromDate, toDate)));
    } catch (error) {
      Alert.error(error.response?.data?.error || ITCL_REQUEST_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, statusParam, unloadOnly]);

  useEffect(() => {
    if (activeTab === 'incoming') setActiveTab('unload');
  }, [activeTab]);

  useEffect(() => {
    if (tabInitializedRef.current || !user) return;
    tabInitializedRef.current = true;
    setActiveTab(defaultItclRequestTab(user));
  }, [user]);

  useEffect(() => {
    fetchRows();
    fetchCounts();
  }, [fetchRows, fetchCounts]);

  const openView = (requestId) => {
    navigate(`/ogcl-transfer-requests/view/${encodeURIComponent(requestId)}`);
  };

  const openReview = (request, action) => {
    setReviewModal({ open: true, action, request });
  };

  const closeReview = () => {
    if (actingId) return;
    setReviewModal({ open: false, action: 'approve', request: null });
  };

  const confirmReview = async (remarks) => {
    const request = reviewModal.request;
    if (!request?.request_id) return;
    try {
      setActingId(request.request_id);
      await ogclTransferService[reviewModal.action](request.request_id, { remarks });
      Alert.success(`Request ${reviewModal.action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewModal({ open: false, action: 'approve', request: null });
      fetchRows();
      fetchCounts();
    } catch (error) {
      Alert.error(error.response?.data?.error || `Failed to ${reviewModal.action} request`);
    } finally {
      setActingId(null);
    }
  };

  const requestedByLabel = (request) =>
    request.requested_by_display
    || formatUsernameLocation(
      request.requested_by_name || request.requested_by_username,
      request.requested_by_hub_name,
      request.requested_by,
    );

  const routeLabel = (request) =>
    `${request.from_hub_name || request.from_hub_id} → ${request.to_hub_name || request.to_hub_id}`;

  const vehicleLabel = (request) =>
    `${request.vehicle_number || '—'}${request.driver_name ? ` / ${request.driver_name}` : ''}`;

  const shipmentCount = (request) => {
    const ids = request.shipment_ids || [];
    const count = request.shipment_count ?? ids.length;
    return `${count} shipment${count === 1 ? '' : 's'}`;
  };

  const requestCanReview = (request) => (
    Boolean(request?.can_review) && request.status === 'PENDING'
  );

  const renderRowActions = (request, menuKey = request.request_id) => (
    <RowActionsMenu
      className="relative menu-container inline-flex"
      isOpen={openMenuId === menuKey}
      onToggle={() => setOpenMenuId(openMenuId === menuKey ? null : menuKey)}
      onClose={() => setOpenMenuId(null)}
      onView={() => openView(request.request_id)}
      viewLabel={requestCanReview(request) ? 'Review' : 'View'}
      extraItems={
        requestCanReview(request) && actingId !== request.request_id
          ? [
              {
                label: 'Approve',
                icon: '✓',
                variant: 'success',
                onClick: () => openReview(request, 'approve'),
              },
              {
                label: 'Reject',
                icon: '✗',
                variant: 'danger',
                onClick: () => openReview(request, 'reject'),
              },
            ]
          : []
      }
    />
  );

  return (
    <DashboardLayout>
      <ListPageHeader
        title={ITCL_REQUEST_TITLE}
        subtitle={ITCL_REQUEST_SUBTITLE}
        icon={Truck}
        backTo={homePath}
      />

      {canCreate && (
        <div className="mb-3 flex justify-end gap-2">
          <Button
            mobileIconOnly
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/ogcl-transfer-requests/create')}
          >
            {ITCL_REQUEST_CREATE}
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
              {visibleTabs.map((tab) => {
                const active = activeTab === tab.id;
                const isUnload = tab.id === 'unload';
                const count = counts[tab.id] || 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {tab.label}
                    {isUnload ? (
                      <>
                        <span
                          title="Pending unload"
                          className={`min-w-[1.1rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                            active ? 'bg-amber-50 text-amber-800' : 'bg-amber-50/80 text-amber-700'
                          }`}
                        >
                          {counts.unloadPending || 0}
                        </span>
                        <span
                          title="Unloaded"
                          className={`min-w-[1.1rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                            active ? 'bg-green-50 text-green-700' : 'bg-green-50/80 text-green-700'
                          }`}
                        >
                          {counts.unloadUnloaded || 0}
                        </span>
                      </>
                    ) : (
                      <span
                        className={`min-w-[1.25rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {!unloadOnly && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:w-auto">
            <div className="min-w-0 sm:w-40">
              <label className="block text-xs font-medium text-gray-600 mb-1">From date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              />
            </div>
            <div className="min-w-0 sm:w-40">
              <label className="block text-xs font-medium text-gray-600 mb-1">To date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {unloadOnly ? (
        <UnloadItclShipmentPanel onCountChange={handleUnloadCount} />
      ) : loading ? (
        <Loader fullScreen={false} message="Loading transfer requests..." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              No {activeTab} transfer requests
            </p>
          ) : (
            <>
              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold">Request</th>
                      <th className="text-left px-4 py-2.5 font-semibold">From → To</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Shipments</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Vehicle</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Requested By</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Submitted</th>
                      <th className="text-center px-4 py-2.5 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((request) => (
                      <tr key={request.request_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium">
                          <button
                            type="button"
                            onClick={() => openView(request.request_id)}
                            className="text-secondary hover:underline text-left"
                          >
                            {request.request_id}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">{routeLabel(request)}</td>
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() => openView(request.request_id)}
                            className="text-left hover:underline"
                          >
                            {shipmentCount(request)}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">{vehicleLabel(request)}</td>
                        <td className="px-4 py-2.5">{requestedByLabel(request)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${statusClass(request.status)}`}>
                            {statusLabel(request.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {formatSubmitted(request.created_at || request.requested_at)}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {renderRowActions(request)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-3 p-3 sm:p-4">
                {rows.map((request) => (
                  <div
                    key={`card-${request.request_id}`}
                    className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-blue-500 shadow-sm p-4"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => openView(request.request_id)}
                        className={`${listIdLinkMobileClass} text-left`}
                      >
                        {request.request_id}
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusClass(request.status)}`}>
                          {statusLabel(request.status)}
                        </span>
                        {renderRowActions(request, `card-${request.request_id}`)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div className="min-w-0 col-span-2">
                        <span className="text-gray-500 block">From → To</span>
                        <p className="font-medium text-gray-800 break-words">{routeLabel(request)}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Shipments</span>
                        <p className="font-medium text-gray-800">{shipmentCount(request)}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Vehicle</span>
                        <p className="font-medium text-gray-800 break-words">{vehicleLabel(request)}</p>
                      </div>
                      <div className="min-w-0 col-span-2">
                        <span className="text-gray-500 block">Requested By</span>
                        <p className="font-medium text-gray-800 break-words">{requestedByLabel(request)}</p>
                      </div>
                      <div className="min-w-0 col-span-2">
                        <span className="text-gray-500 block">Submitted</span>
                        <p className="font-medium text-gray-800">
                          {formatSubmitted(request.created_at || request.requested_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <OgclReviewConfirmModal
        open={reviewModal.open}
        action={reviewModal.action}
        requestId={reviewModal.request?.request_id}
        loading={Boolean(actingId)}
        onClose={closeReview}
        onConfirm={confirmReview}
      />
    </DashboardLayout>
  );
};

export default OgclTransferRequestList;
